(() => {
  'use strict';
  if (window.__livearoAccessoriesBound) return;
  window.__livearoAccessoriesBound = true;

  const STORAGE_KEY = 'livearo-accessory-selections-v1';
  const QUERY_KEY = 'accessories';
  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const language = document.documentElement.lang.toLowerCase().startsWith('zh') ? 'zh'
    : document.documentElement.lang.toLowerCase().startsWith('en') ? 'en' : 'ja';
  const local = value => typeof value === 'string' ? value : String(value?.[language] || value?.ja || value?.en || '');
  const copy = {
    heading: { ja: 'レール・取付部品のご希望', en: 'Rail and accessory preferences', zh: '轨道与配件需求清单' },
    empty: { ja: '選択したレール・取付部品がここに表示されます。', en: 'Your selected rails and accessories will appear here.', zh: '您选择的轨道和配件会显示在这里。' },
    product: { ja: '製品', en: 'Product', zh: '产品' },
    group: { ja: '項目', en: 'Option group', zh: '选项类别' },
    selected: { ja: 'ご希望の仕様', en: 'Selected options', zh: '已选配置' },
    actions: { ja: '編集', en: 'Edit', zh: '编辑' },
    remove: { ja: '削除', en: 'Remove', zh: '移除' },
    removeProduct: { ja: '「{product}」の選択を削除', en: 'Remove selections for {product}', zh: '移除“{product}”的配置' },
    count: { ja: '{count}項目選択', en: '{count} selected', zh: '已选 {count} 项' },
    unselected: { ja: '未選択', en: 'Optional', zh: '选填' },
    saved: { ja: '選択内容を相談リストに保存しました。', en: 'Your selections have been saved to the enquiry list.', zh: '已将配置保存到咨询清单。' },
    removed: { ja: 'この製品の選択を削除しました。', en: 'The selections for this product have been removed.', zh: '已移除该产品的配置。' }
  };
  const groups = new Map();
  const products = new Map();
  let selections = [];
  let hasLocalState = false;
  let observer;

  function text(key, replacements = {}) {
    const dataKey = key === 'heading' ? 'tableHeading' : key === 'empty' ? 'noRequests' : key;
    return local(window.LIVEARO_ACCESSORIES?.ui?.[dataKey] || copy[key]).replace(/\{(\w+)\}/g, (match, name) =>
      Object.prototype.hasOwnProperty.call(replacements, name) ? String(replacements[name]) : match);
  }

  function readData() {
    groups.clear(); products.clear();
    const data = window.LIVEARO_ACCESSORIES;
    if (!Array.isArray(data?.groups) || !Array.isArray(data?.products)) return;
    data.groups.forEach(group => {
      if (typeof group?.id !== 'string' || groups.has(group.id) || !Array.isArray(group.options)) return;
      const options = new Map();
      group.options.forEach(option => {
        if (typeof option?.id === 'string' && local(option.label) && !options.has(option.id)) options.set(option.id, option);
      });
      groups.set(group.id, { ...group, options, multiple: group.multiple === true });
    });
    data.products.forEach(product => {
      if (typeof product?.id !== 'string' || products.has(product.id) || !local(product.title) || !Array.isArray(product.groups)) return;
      const available = new Map();
      product.groups.forEach(entry => {
        const group = groups.get(entry?.groupId);
        if (!group || available.has(group.id) || !Array.isArray(entry.optionIds)) return;
        const ids = [...new Set(entry.optionIds.filter(id => typeof id === 'string' && group.options.has(id)))];
        if (ids.length) available.set(group.id, ids);
      });
      products.set(product.id, { ...product, available });
    });
  }

  function normalize(value) {
    if (!Array.isArray(value)) return [];
    const requested = new Map();
    value.slice(0, 200).forEach(entry => {
      if (!entry || typeof entry.productId !== 'string' || !products.has(entry.productId) || !Array.isArray(entry.options)) return;
      if (!requested.has(entry.productId)) requested.set(entry.productId, new Set());
      entry.options.slice(0, 200).forEach(id => { if (typeof id === 'string') requested.get(entry.productId).add(id); });
    });
    const result = [];
    products.forEach(product => {
      const ids = requested.get(product.id);
      if (!ids) return;
      const options = [];
      product.available.forEach((allowed, groupId) => {
        const selected = allowed.filter(id => ids.has(id));
        options.push(...(groups.get(groupId).multiple ? selected : selected.slice(0, 1)));
      });
      if (options.length) result.push({ productId: product.id, options: [...new Set(options)] });
    });
    return result;
  }

  function parse(value) {
    try { return normalize(JSON.parse(value || '[]')); }
    catch (_) { return []; }
  }

  function currentQuery() {
    try { return new URL(location.href).searchParams.get(QUERY_KEY); }
    catch (_) { return null; }
  }

  function load() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      hasLocalState = saved !== null;
      if (hasLocalState) return parse(saved);
    } catch (_) { /* URL state also supports browsers with storage disabled. */ }
    const requested = currentQuery();
    if (requested !== null) {
      hasLocalState = true;
      return parse(requested);
    }
    return [];
  }

  function save() {
    hasLocalState = true;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(selections)); }
    catch (_) { /* Selections remain available in memory and in enquiry URLs. */ }
  }

  window.LIVEARO_ACCESSORY_SELECTIONS = () => selections.map(entry => ({ productId: entry.productId, options: entry.options.slice() }));

  function replaceQuery() {
    try {
      const url = new URL(location.href);
      if (selections.length) url.searchParams.set(QUERY_KEY, JSON.stringify(selections));
      else url.searchParams.delete(QUERY_KEY);
      if (url.href !== location.href) history.replaceState(history.state, '', url);
    } catch (_) { /* Sandboxed previews can forbid changing the parent URL. */ }
  }

  function isContactLink(anchor) {
    if (anchor.matches('[data-accessory-contact], [data-specialty-inquiry], [data-vertical-inquiry], [data-colors-inquiry], [data-fabrics-inquiry], [data-sample-inquiry]')) return true;
    try {
      const url = new URL(anchor.getAttribute('href'), location.href);
      if (!['http:', 'https:'].includes(url.protocol) || url.origin !== location.origin) return false;
      if (/(?:^|\/)contact(?:\.html)?\/?$/.test(url.pathname)) return true;
      return all('[data-contact-url]').some(node => {
        try { return new URL(node.dataset.contactUrl, location.href).pathname === url.pathname; }
        catch (_) { return false; }
      });
    } catch (_) { return false; }
  }

  function updateLink(anchor) {
    if (!anchor.hasAttribute('href') || anchor.getAttribute('aria-disabled') === 'true' || !isContactLink(anchor)) return;
    try {
      const url = new URL(anchor.getAttribute('href'), location.href);
      if (!['http:', 'https:'].includes(url.protocol)) return;
      if (selections.length) url.searchParams.set(QUERY_KEY, JSON.stringify(selections));
      else url.searchParams.delete(QUERY_KEY);
      if (anchor.href !== url.href) anchor.href = url.href;
    } catch (_) { /* Keep malformed links under their original component's control. */ }
  }

  function updateLinks(root = document) {
    if (root.matches?.('a')) updateLink(root);
    all('a[href]', root).forEach(updateLink);
  }

  function groupSelections(entry) {
    const product = products.get(entry.productId);
    if (!product) return [];
    const selected = new Set(entry.options);
    const result = [];
    product.available.forEach((allowed, groupId) => {
      const group = groups.get(groupId);
      const options = allowed.filter(id => selected.has(id)).map(id => local(group.options.get(id).label));
      if (options.length) result.push({ title: local(group.title), options });
    });
    return result;
  }

  function submissionText() {
    return selections.map(entry => {
      const title = local(products.get(entry.productId).title);
      const rows = groupSelections(entry).map(group => '  ' + group.title + ': ' + group.options.join(' / '));
      return [title, ...rows].join('\n');
    }).join('\n\n');
  }

  function element(tag, className, value) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = value;
    return node;
  }

  function renderInquiry() {
    all('[data-accessory-inquiry]').forEach((mount, index) => {
      const section = element('section', 'accessory-inquiry');
      const heading = element('h2', 'accessory-inquiry-heading', text('heading'));
      heading.id = 'accessory-inquiry-heading-' + index;
      heading.tabIndex = -1;
      section.setAttribute('aria-labelledby', heading.id);
      section.append(heading);
      if (!selections.length) section.append(element('p', 'accessory-inquiry-empty', text('empty')));
      else {
        const table = element('table', 'accessory-inquiry-table');
        table.setAttribute('aria-labelledby', heading.id);
        const head = document.createElement('thead');
        const row = document.createElement('tr');
        ['product', 'group', 'selected', 'actions'].forEach(key => {
          const cell = element('th', '', text(key)); cell.scope = 'col'; row.append(cell);
        });
        head.append(row); table.append(head);
        selections.forEach(entry => {
          const rows = groupSelections(entry);
          const body = document.createElement('tbody');
          const title = local(products.get(entry.productId).title);
          rows.forEach((group, rowIndex) => {
            const tr = document.createElement('tr');
            if (rowIndex === 0) {
              const product = element('th', 'accessory-inquiry-product', title);
              product.scope = 'rowgroup'; product.rowSpan = rows.length; tr.append(product);
            }
            const name = element('th', 'accessory-inquiry-group', group.title);
            name.scope = 'row'; tr.append(name);
            const options = element('td', 'accessory-inquiry-values', group.options.join(' / '));
            tr.append(options);
            if (rowIndex === 0) {
              const actions = element('td', 'accessory-inquiry-actions');
              actions.rowSpan = rows.length;
              const remove = element('button', 'accessory-inquiry-remove', text('remove'));
              remove.type = 'button'; remove.dataset.accessoryRemove = entry.productId;
              remove.setAttribute('aria-label', text('removeProduct', { product: title }));
              actions.append(remove); tr.append(actions);
            }
            body.append(tr);
          });
          table.append(body);
        });
        section.append(table);
      }
      const status = element('p', 'accessory-status');
      status.dataset.accessoryInquiryStatus = '';
      status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
      section.append(status);
      const field = document.createElement('textarea');
      field.hidden = true; field.readOnly = true; field.dataset.accessorySubmission = '';
      field.name = mount.closest('form')?.matches('[data-preview-form]') ? text('heading') : 'contact[Rail and accessory preferences]';
      field.value = submissionText();
      section.append(field);
      mount.replaceChildren(section);
    });
  }

  function renderControls() {
    all('[data-accessory-options]').forEach(panel => {
      const product = products.get(panel.dataset.accessoryOptions);
      const current = selections.find(entry => entry.productId === product?.id);
      const selected = new Set(current?.options || []);
      all('[data-accessory-count]', panel).forEach(node => {
        node.textContent = text(selected.size ? 'count' : 'unselected', { count: selected.size });
      });
      all('[data-accessory-option]', panel).forEach(button => {
        button.type = 'button';
        const groupId = button.closest('[data-accessory-group]')?.dataset.accessoryGroup;
        const valid = Boolean(product?.available.get(groupId)?.includes(button.dataset.accessoryOption));
        button.disabled = !valid;
        button.setAttribute('aria-pressed', String(valid && selected.has(button.dataset.accessoryOption)));
      });
      if (!panel.querySelector('[data-accessory-status]')) {
        const status = element('p', 'accessory-status');
        status.dataset.accessoryStatus = '';
        status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
        panel.append(status);
      }
    });
  }

  function render() {
    renderControls(); renderInquiry(); replaceQuery(); updateLinks();
    document.dispatchEvent(new CustomEvent('livearo:accessory-selection-updated', {
      detail: { count: selections.length, selections: window.LIVEARO_ACCESSORY_SELECTIONS() }
    }));
  }

  function toggle(button) {
    const panel = button.closest('[data-accessory-options]');
    const product = products.get(panel?.dataset.accessoryOptions);
    const groupId = button.closest('[data-accessory-group]')?.dataset.accessoryGroup;
    const allowed = product?.available.get(groupId);
    const optionId = button.dataset.accessoryOption;
    if (!allowed?.includes(optionId)) return;
    const current = selections.find(entry => entry.productId === product.id);
    let options = current?.options.slice() || [];
    if (options.includes(optionId)) options = options.filter(id => id !== optionId);
    else {
      if (!groups.get(groupId).multiple) options = options.filter(id => !allowed.includes(id));
      options.push(optionId);
    }
    selections = normalize([...selections.filter(entry => entry.productId !== product.id), { productId: product.id, options }]);
    save(); render();
    all('[data-accessory-status]', panel).forEach(node => { node.textContent = text('saved'); });
  }

  function preserveLanguageQuery(event) {
    const select = event.target.closest?.('[data-language-select]');
    const form = select?.form || (event.type === 'submit' && event.target.querySelector?.('[data-language-select]') ? event.target : null);
    if (!form || (!selections.length && !hasLocalState)) return;
    let input = form.querySelector('input[name="return_to"]');
    if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'return_to'; form.append(input); }
    input.value = location.pathname + location.search + location.hash;
  }

  function start() {
    readData(); selections = load();
    if (hasLocalState) save();
    render();
    document.addEventListener('click', event => {
      const button = event.target.closest?.('[data-accessory-option], [data-accessory-remove]');
      if (button) {
        event.preventDefault();
        if (button.disabled) return;
        if (button.matches('[data-accessory-option]')) toggle(button);
        else {
          const mount = button.closest('[data-accessory-inquiry]');
          const id = button.dataset.accessoryRemove;
          if (!selections.some(entry => entry.productId === id)) return;
          selections = selections.filter(entry => entry.productId !== id);
          save(); render();
          mount?.querySelector('[data-accessory-remove], .accessory-inquiry-heading')?.focus();
          all('[data-accessory-inquiry-status]').forEach(node => { node.textContent = text('removed'); });
        }
        return;
      }
      const anchor = event.target.closest?.('a');
      if (anchor) updateLink(anchor);
    });
    document.addEventListener('auxclick', event => {
      const anchor = event.target.closest?.('a'); if (anchor) updateLink(anchor);
    });
    document.addEventListener('change', preserveLanguageQuery, true);
    document.addEventListener('submit', event => {
      preserveLanguageQuery(event);
      // Update the field before either the preview's FormData export or Shopify's native submission.
      all('[data-accessory-submission]', event.target).forEach(field => { field.value = submissionText(); });
    }, true);
    document.addEventListener('shopify:section:load', () => {
      readData(); selections = normalize(selections); render();
    });
    window.addEventListener('storage', event => {
      if (event.key !== STORAGE_KEY && event.key !== null) return;
      hasLocalState = true;
      selections = event.key === null ? [] : parse(event.newValue);
      render();
    });
    window.addEventListener('popstate', () => { replaceQuery(); updateLinks(); });
    observer = new MutationObserver(records => {
      records.forEach(record => {
        if (record.type === 'attributes') updateLink(record.target);
        else record.addedNodes.forEach(node => { if (node.nodeType === 1) updateLinks(node); });
      });
    });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['href'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
